import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-[color,background-color,border-color,box-shadow,transform] duration-150 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "bg-primary bg-linear-to-b from-white/15 to-transparent text-primary-foreground shadow-[0_1px_2px_rgba(0,0,0,0.2),0_0_0_1px_rgba(0,0,0,0.06)] hover:bg-primary/90",
        destructive:
          "bg-destructive bg-linear-to-b from-white/15 to-transparent text-destructive-foreground shadow-[0_1px_2px_rgba(0,0,0,0.2),0_0_0_1px_rgba(0,0,0,0.06)] hover:bg-destructive/90",
        destructiveOutline:
          "border border-destructive text-destructive bg-transparent hover:bg-destructive/10",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-white/[0.04] dark:border-white/15 dark:hover:bg-white/[0.08]",
        secondary:
          "bg-secondary border text-secondary-foreground hover:bg-secondary/80 dark:bg-white/[0.12] dark:border-white/10 dark:hover:bg-white/[0.16]",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-7 rounded-md px-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        reset: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = (
  {
    className,
    variant,
    size,
    asChild = false,
    ...props
  }: ButtonProps
) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
    
      {...props}
    />
  )
}
Button.displayName = "Button"

export { Button, buttonVariants }
